/* Помощники экранов инструментов без разметки: выход из итога, «Попробовать ещё раз», баланс и подписи цены. */
import { useEffect, useRef, useState } from 'react';
import { endpoints } from '../../../api';
import { uiFormat, uiText } from '../../../i18n';
import type { CircleFormState } from './CircleFieldInput';
import { cNum } from './order-tail-model';
import { priceUnit } from '../studio-content';

/** Отметить итог просмотренным (03-api.md §9.8) — ошибка на экран не влияет. */
export function markSeen(orderId: number | undefined) {
  if (!orderId) return;
  void endpoints.history.seen(orderId).catch(() => {});
}
/** Выйти из итога к форме с теми же настройками («Ещё вариант», «Изменить», «Заменить запись»). */
export async function leaveTail(state: CircleFormState) {
  const id = state.orderId;
  if (await state.returnToForm()) {
    markSeen(id);
    document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id } }));
    return true;
  }
  return false;
}
/** «Попробовать ещё раз» после сбоя (§8.5): форма вернулась — новый заказ с тем же входом и новым ключом.
    Хук живёт в форме экрана (она не размонтируется), а не в хвосте, который уходит вместе с заказом.
    Скидка сбойного заказа («Сделать дешевле», если форма её публикует) остаётся и у нового: тот же вход — та же цена. */
export function useTailRetry(state: CircleFormState) {
  const wanted = useRef(false);
  const cheap = useRef(false);
  const { orderId, form, busy, submit } = state;
  useEffect(() => {
    if (!wanted.current || orderId || !form || busy) return;
    wanted.current = false;
    void (cheap.current ? submit({ cheap: true }) : submit());
  });
  return () => {
    wanted.current = true;
    cheap.current = !!state.intent?.body.cheap;
    void leaveTail(state).then(ok => {
      if (!ok) wanted.current = false;
    });
  };
}
/** Остаток Circle для «Не хватает Circle» (GET /app/balance) — одна память на страницу: у родной кнопки (useCircleBalance)
    и у движка шаблона (voice-server, ui.balance). Свежий снимок Me (steos:account-updated: пополнение, слияние) кладёт свой остаток. */
let balanceCache: { at: number; value: number } | null = null;
const BALANCE_FRESH_MS = 60000;
/** Остаток не старше минуты; null — неизвестен или устарел */
export function circleBalance(): number | null {
  return balanceCache && Date.now() - balanceCache.at < BALANCE_FRESH_MS ? balanceCache.value : null;
}
export function rememberCircleBalance(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) balanceCache = { at: Date.now(), value };
}
/** Остаток устарел (создан заказ — сервер списал Circle): следующий показ кнопки спросит его заново */
export function forgetCircleBalance() {
  balanceCache = null;
}
if (typeof document !== 'undefined')
  document.addEventListener('steos:account-updated', event => {
    const circle = (event as CustomEvent<{ balance?: { circle?: unknown } } | undefined>).detail?.balance?.circle;
    if (typeof circle === 'number' && Number.isFinite(circle)) rememberCircleBalance(circle);
  });
/** Баланс для «Не хватает Circle»: один запрос на минуту; ошибка — проверки нет (решает сервер, 402). */
export function useCircleBalance(enabled: boolean) {
  const [balance, setBalance] = useState<number | null>(circleBalance);
  useEffect(() => {
    if (!enabled || circleBalance() !== null) return;
    let active = true;
    void endpoints
      .balance()
      .then(value => {
        if (!active || !Number.isFinite(value?.circle)) return;
        rememberCircleBalance(value.circle);
        setBalance(value.circle);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [enabled]);
  return balance;
}
/** «Получить текст · 4 C», «Сменить голос · от 10 C»; цена неизвестна или работа бесплатная — без суммы. */
export function toolPriceLabel(label: string, price: number | null, from = false, chargeable = true) {
  if (price === null || !chargeable) return uiText(label);
  return from ? uiFormat('{0} · от {1} C', uiText(label), cNum(price)) : uiFormat('{0} · {1} C', uiText(label), cNum(price));
}
/** Цена за единицу подзаголовком шапки: «1 C в минуту»; нет числа — пусто. */
export function unitText(amount: number | null | undefined, unit: string) {
  return Number.isFinite(amount) && Number(amount) > 0 ? `${cNum(Number(amount))} ${uiText(unit)}` : '';
}
/** Цена из app.price / ToolPrice (03-api.md §6.7): {amount, unit, from}. */
export function toolPrice(value: unknown): { amount: number | null; unit: string | null; from: boolean } {
  if (typeof value === 'number') return { amount: Number.isFinite(value) ? value : null, unit: null, from: false };
  const price = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const amount = Number(price.amount ?? price.circle);
  return {
    amount: Number.isFinite(amount) ? amount : null,
    unit: priceUnit(price) || null,
    from: price.from === true,
  };
}
/** Единица ToolPrice словами: per_minute — «C в минуту», per_second — «C за секунду», per_100_chars — «C за 100 знаков». */
export function unitWords(unit: string | null, fallback: string) {
  return unit === 'per_minute' ? 'C в минуту' : unit === 'per_second' ? 'C за секунду' : unit === 'per_100_chars' ? 'C за 100 знаков' : fallback;
}
